/**
 * CierreTicketWebTablas.tsx — Tablas reutilizables del ticket de cierre (vista web).
 *
 * Patrón canónico: rejilla con separadores horizontales solo entre bloques (no por fila).
 * Compartido por ventas, caja física, propinas, arqueo, top productos, monitor ciego, etc.
 */

import { Fragment, type ReactNode } from "react";
import type { CierreAuditMetrics } from "../utils/cierreAuditMetrics";
import type {
  BaseAperturaTurno,
  CierreCaja,
  ConteoEfectivo,
} from "../types/caja.types";
import type { CierreTicketModel } from "../utils/buildCierreTicketModel";
import {
  gastoExternoMetodoLabel,
  type GastoExterno,
} from "../utils/externalExpensesHelper";
import { formatCierreHourAmPm } from "../utils/cierreCuadranteHelper";
import {
  formatLineaDenominacionConteo,
  lineasConteoEfectivo,
  partitionBilletesMonedas,
} from "../utils/conteoEfectivoLineas";
import { formatTicketMoney } from "../utils/ticketCierreFormat";
import {
  METODOS_PROPINA,
  type MontosPorMetodoPago,
} from "../utils/propinaAllocation";
import { getDefaultPaymentMethodLabel } from "@/lib/paymentMethodsConfig";
import {
  TICKET_CIERRE_TABLA_COL_SEP,
  TICKET_CIERRE_TABLA_COLUMNAS_METODO_INGRESOS,
  TICKET_CIERRE_TABLA_DATA_CELL_COMPACTA,
  TICKET_CIERRE_TABLA_DATA_METODO_DER,
  TICKET_CIERRE_TABLA_DATA_METODO_IZQ,
  TICKET_CIERRE_TABLA_GRID,
  TICKET_CIERRE_TABLA_HEAD_CELL_COMPACTA,
  TICKET_CIERRE_TABLA_HEAD_METODO_DER,
  TICKET_CIERRE_TABLA_HEAD_METODO_IZQ,
  TICKET_CIERRE_META_ROW,
  TICKET_CIERRE_SECTION_TITLE,
  TICKET_CIERRE_SUBSECTION_LABEL,
  TICKET_CIERRE_TABLA_COLUMNAS_RENDIMIENTO_EMPLEADO,
  TICKET_CIERRE_TABLA_HR_FILA,
  TICKET_CIERRE_TABLA_HR_PROPINA_EMPLEADO,
  TICKET_CIERRE_TABLA_MONO_BASE,
} from "../constants/ticketCierreTablaRejilla";

export type Tabla2ColFila = {
  label: string;
  value: string;
  strong?: boolean;
  valueClassName?: string;
};

const BILLETES_MONEDAS_COLS = {
  gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
} as const;

/** Título de sección centrado (sin líneas extra arriba/abajo). */
export function TicketSectionTitle({ title }: { title: string }) {
  return <p className={TICKET_CIERRE_SECTION_TITLE}>{title}</p>;
}

/** @deprecated Usar `TicketSectionTitle` — mantiene compatibilidad sin `<hr>`. */
export function TicketSectionSubHeading({ title }: { title: string }) {
  return <TicketSectionTitle title={title} />;
}

/** @deprecated Usar `TicketSectionTitle` — mantiene compatibilidad sin `<hr>`. */
export function TicketSectionMajorHeading({ title }: { title: string }) {
  return <TicketSectionTitle title={title} />;
}

/** Etiqueta de sub-bloque dentro de una sección (auditoría recaudo, etc.). */
export function TicketSubsectionLabel({ children }: { children: string }) {
  return <p className={TICKET_CIERRE_SUBSECTION_LABEL}>{children}</p>;
}

/** Meta «Etiqueta: valor» en una línea (hora pico, apertura en bloques compactos). */
export function TicketMetaRow({
  label,
  value,
  valueClassName = "font-bold tabular-nums shrink-0 whitespace-nowrap",
}: {
  label: string;
  value: ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className={TICKET_CIERRE_META_ROW}>
      <span className="min-w-0 break-words text-left">{label}</span>
      <span className={valueClassName}>{value}</span>
    </div>
  );
}

function renderFila2Col(row: Tabla2ColFila, sep: string) {
  return (
    <>
      <div
        className={`${TICKET_CIERRE_TABLA_DATA_METODO_IZQ} ${row.strong ? "font-black" : "font-medium"} ${sep}`}
      >
        {row.label}
      </div>
      <div
        className={`${TICKET_CIERRE_TABLA_DATA_METODO_DER} ${row.strong ? "font-black" : ""} ${row.valueClassName ?? ""}`}
      >
        {row.value}
      </div>
    </>
  );
}

/** Tabla genérica 2 columnas con N bloques separados por línea horizontal. */
export function TicketTabla2ColGrupos({
  colIzqHeader,
  colDerHeader,
  grupos,
  showHeader = true,
  closeBottom = true,
}: {
  colIzqHeader: string;
  colDerHeader: string;
  grupos: Tabla2ColFila[][];
  showHeader?: boolean;
  closeBottom?: boolean;
}) {
  const sep = TICKET_CIERRE_TABLA_COL_SEP;

  return (
    <div className={`${TICKET_CIERRE_TABLA_MONO_BASE} leading-tight`}>
      <div
        className={`${TICKET_CIERRE_TABLA_GRID} border-t border-solid border-gray-800`}
        style={TICKET_CIERRE_TABLA_COLUMNAS_METODO_INGRESOS}
      >
        {showHeader ? (
          <>
            <div className={`${TICKET_CIERRE_TABLA_HEAD_METODO_IZQ} ${sep}`}>
              {colIzqHeader}
            </div>
            <div className={TICKET_CIERRE_TABLA_HEAD_METODO_DER}>
              {colDerHeader}
            </div>
            <hr className={TICKET_CIERRE_TABLA_HR_FILA} aria-hidden />
          </>
        ) : null}

        {grupos.map((grupo, gi) => (
          <Fragment key={`grupo-${gi}`}>
            {grupo.map((row) => (
              <Fragment key={row.label}>{renderFila2Col(row, sep)}</Fragment>
            ))}
            {gi < grupos.length - 1 ? (
              <hr className={TICKET_CIERRE_TABLA_HR_FILA} aria-hidden />
            ) : null}
          </Fragment>
        ))}

        {closeBottom ? (
          <hr className={TICKET_CIERRE_TABLA_HR_FILA} aria-hidden />
        ) : null}
      </div>
    </div>
  );
}

export function TicketTablaVentasPorMetodo({
  model,
}: {
  model: CierreTicketModel;
}) {
  const metodos = METODOS_PROPINA.map(
    (k) =>
      [k, getDefaultPaymentMethodLabel(k)] as [
        keyof MontosPorMetodoPago,
        string,
      ],
  )
    .filter(([k]) => model.ventasCobradas[k] > 0)
    .map(([k, label]) => ({
      label,
      value: formatTicketMoney(model.ventasCobradas[k]),
    }));

  const subtotalesCanales: Tabla2ColFila[] = [];
  if (model.ventasCobradas.efectivo > 0) {
    subtotalesCanales.push({
      label: "Total Efectivo",
      value: formatTicketMoney(model.ventasCobradas.efectivo),
    });
  }
  if (model.totalDigitalCobrado > 0) {
    subtotalesCanales.push({
      label: "Total Digital",
      value: formatTicketMoney(model.totalDigitalCobrado),
    });
  }

  const totalCobrado = Object.values(model.ventasCobradas).reduce(
    (total, monto) => total + monto,
    0,
  );
  const totalVentasCobradas: Tabla2ColFila[] = [
    {
      label: "TOTAL COBRADO EN VENTAS",
      value: formatTicketMoney(totalCobrado),
      strong: true,
    },
  ];

  const grupos: Tabla2ColFila[][] = [[...metodos]];
  grupos.push(subtotalesCanales, totalVentasCobradas);
  // Línea informativa: recaudo total del turno (incluye ingresos manuales,
  // abonos a creditos y propinas). No altera el cuadre — se muestra aparte.
  if (model.totalRecaudado > totalCobrado) {
    grupos.push([
      {
        label: "Recaudo total (incl. ingresos/abonos)",
        value: formatTicketMoney(model.totalRecaudado),
        valueClassName: "text-gray-500",
      },
    ]);
  }

  return (
    <TicketTabla2ColGrupos
      colIzqHeader="Método de Pago"
      colDerHeader="Total"
      grupos={grupos}
      closeBottom={false}
    />
  );
}

/** Tabla genérica cobrado por método (jornada global, sub-bloques de caja/turno). */
export function TicketTablaCobradoPorMetodo({
  montos,
  subtotalLabel = "SUBTOTAL",
  incluirSubtotalesCanal = false,
}: {
  montos: MontosPorMetodoPago;
  subtotalLabel?: string;
  incluirSubtotalesCanal?: boolean;
}) {
  const metodos = METODOS_PROPINA.map(
    (k) =>
      [k, getDefaultPaymentMethodLabel(k)] as [
        keyof MontosPorMetodoPago,
        string,
      ],
  )
    .filter(([k]) => montos[k] > 0)
    .map(([k, label]) => ({
      label,
      value: formatTicketMoney(montos[k]),
    }));

  const subtotal = METODOS_PROPINA.reduce((s, k) => s + montos[k], 0);
  const digital = montos.nequi + montos.daviplata + montos.tarjeta;

  const subtotalesCanales: Tabla2ColFila[] = [];
  if (incluirSubtotalesCanal) {
    if (montos.efectivo > 0) {
      subtotalesCanales.push({
        label: "Total Efectivo (Ventas)",
        value: formatTicketMoney(montos.efectivo),
      });
    }
    if (digital > 0) {
      subtotalesCanales.push({
        label: "Total Digital (Ventas)",
        value: formatTicketMoney(digital),
      });
    }
  }

  const pie: Tabla2ColFila[] = [
    {
      label: subtotalLabel,
      value: formatTicketMoney(subtotal),
      strong: true,
    },
  ];

  return (
    <TicketTabla2ColGrupos
      colIzqHeader="Método de Pago"
      colDerHeader="Total"
      grupos={[[...metodos, ...subtotalesCanales], pie]}
      closeBottom={false}
    />
  );
}

export function TicketTablaCajaFisica({
  ingresos,
  egresos,
  esperado,
}: {
  ingresos: Tabla2ColFila[];
  egresos: Tabla2ColFila[];
  esperado: number;
}) {
  const grupos: Tabla2ColFila[][] = [ingresos];
  if (egresos.length > 0) grupos.push(egresos);

  const cierre: Tabla2ColFila[] = [
    {
      label: "Efectivo esperado en gaveta",
      value: formatTicketMoney(esperado),
      strong: true,
    },
  ];
  grupos.push(cierre);

  return (
    <TicketTabla2ColGrupos
      colIzqHeader="Concepto de Flujo"
      colDerHeader="Valor"
      grupos={grupos}
      closeBottom={false}
    />
  );
}

export function TicketTablaBilletesMonedas({
  conteo,
  totalLabel,
}: {
  conteo: ConteoEfectivo;
  totalLabel: string;
}) {
  const { bills, coins } = partitionBilletesMonedas(
    lineasConteoEfectivo(conteo),
  );
  const totalBilletes = bills.reduce((total, linea) => total + linea.sub, 0);
  const totalMonedas = coins.reduce((total, linea) => total + linea.sub, 0);
  const maxRows = Math.max(bills.length, coins.length, 1);
  const sep = TICKET_CIERRE_TABLA_COL_SEP;
  const cell =
    "ticket-cierre-grid-cell px-1 sm:px-1.5 py-0.5 sm:py-1 text-left leading-tight tabular-nums text-[7px] sm:text-[8px] text-gray-900 whitespace-normal break-words min-w-0";

  return (
    <div className={`${TICKET_CIERRE_TABLA_MONO_BASE} leading-tight`}>
      <div
        className={`${TICKET_CIERRE_TABLA_GRID} border-t border-solid border-gray-800`}
        style={BILLETES_MONEDAS_COLS}
      >
        <div className={`${TICKET_CIERRE_TABLA_HEAD_METODO_IZQ} ${sep}`}>
          Billetes
        </div>
        <div className={TICKET_CIERRE_TABLA_HEAD_METODO_DER}>Monedas</div>
        <hr className={TICKET_CIERRE_TABLA_HR_FILA} aria-hidden />

        {Array.from({ length: maxRows }, (_, i) => (
          <Fragment key={`denom-${i}`}>
            <div className={`${cell} ${sep}`}>
              {bills[i] ? formatLineaDenominacionConteo(bills[i]) : ""}
            </div>
            <div className={cell}>
              {coins[i] ? formatLineaDenominacionConteo(coins[i]) : ""}
            </div>
          </Fragment>
        ))}

        <hr className={TICKET_CIERRE_TABLA_HR_FILA} aria-hidden />

        <div className={`${cell} font-black ${sep}`}>
          Total billetes: {formatTicketMoney(totalBilletes)}
        </div>
        <div className={`${cell} font-black`}>
          Total monedas: {formatTicketMoney(totalMonedas)}
        </div>
        <hr className={TICKET_CIERRE_TABLA_HR_FILA} aria-hidden />

        <div className="ticket-cierre-grid-cell col-start-1 [grid-column:1/-1] flex justify-between gap-2 px-1 sm:px-1.5 py-0.5 sm:py-1 font-black tabular-nums text-[8px] sm:text-[9px]">
          <span>{totalLabel}</span>
          <span>{formatTicketMoney(conteo.total)}</span>
        </div>
      </div>
    </div>
  );
}

export function TicketTablaPropinasCobradas({
  propinas,
  totalPropinas,
}: {
  propinas: MontosPorMetodoPago;
  totalPropinas: number;
}) {
  const metodos = METODOS_PROPINA.filter((m) => propinas[m] > 0).map((m) => ({
    label: getDefaultPaymentMethodLabel(m),
    value: formatTicketMoney(propinas[m]),
  }));

  return (
    <TicketTabla2ColGrupos
      colIzqHeader="Método de Pago"
      colDerHeader="Ingreso"
      grupos={[
        metodos,
        [
          {
            label: "Total Propinas",
            value: formatTicketMoney(totalPropinas),
            strong: true,
          },
        ],
      ]}
      closeBottom={false}
    />
  );
}

export function TicketTablaTopProductos({
  productos,
  totalUnidades,
  ventasActivasCount,
}: {
  productos: CierreTicketModel["resumenOperativo"]["topProductos"];
  totalUnidades: number;
  ventasActivasCount: number;
}) {
  const head = TICKET_CIERRE_TABLA_HEAD_CELL_COMPACTA;
  const cell = TICKET_CIERRE_TABLA_DATA_CELL_COMPACTA;
  const sep = TICKET_CIERRE_TABLA_COL_SEP;
  const colStyle = {
    gridTemplateColumns:
      "minmax(1.5rem,auto) minmax(0,1fr) minmax(3.5rem,auto)",
  };

  return (
    <div className={`${TICKET_CIERRE_TABLA_MONO_BASE} leading-tight`}>
      <div
        className={`${TICKET_CIERRE_TABLA_GRID} border-t border-solid border-gray-800`}
        style={colStyle}
      >
        <div className={`${head} tabular-nums whitespace-nowrap ${sep}`}>
          Cant.
        </div>
        <div className={`${head} break-words min-w-0 ${sep}`}>Producto</div>
        <div className={`${head} tabular-nums whitespace-nowrap`}>Total</div>
        <hr className={TICKET_CIERRE_TABLA_HR_FILA} aria-hidden />

        {productos.map((p) => (
          <Fragment key={`${p.rank}-${p.nombre}`}>
            <div
              className={`${cell} tabular-nums text-gray-900 whitespace-nowrap ${sep}`}
            >
              {p.unidades}
            </div>
            <div
              className={`${cell} break-words min-w-0 font-medium text-gray-900 ${sep}`}
            >
              {p.nombre}
            </div>
            <div
              className={`${cell} tabular-nums font-black whitespace-nowrap`}
            >
              {formatTicketMoney(p.subtotal)}
            </div>
          </Fragment>
        ))}

        <hr className={TICKET_CIERRE_TABLA_HR_FILA} aria-hidden />

        <div
          className={`${cell} font-bold text-left justify-start ${sep} col-span-2`}
        >
          Total unidades top 5
        </div>
        <div className={`${cell} tabular-nums font-black whitespace-nowrap`}>
          {totalUnidades} uds
        </div>

        <div
          className={`${cell} font-bold text-left justify-start ${sep} col-span-2`}
        >
          Ventas del turno
        </div>
        <div className={`${cell} tabular-nums font-black whitespace-nowrap`}>
          {ventasActivasCount} v
        </div>
      </div>
    </div>
  );
}

export function TicketTablaIntentosArqueoCiego({
  intentos,
  montosFallback,
  fallbackHoraIso,
}: {
  intentos: NonNullable<CierreCaja["cierreCiegoAudit"]>["intentos"];
  montosFallback: number[];
  fallbackHoraIso: string;
}) {
  const head = TICKET_CIERRE_TABLA_HEAD_CELL_COMPACTA;
  const cell = TICKET_CIERRE_TABLA_DATA_CELL_COMPACTA;
  const sep = TICKET_CIERRE_TABLA_COL_SEP;
  const colStyle = {
    gridTemplateColumns:
      "minmax(1.5rem,auto) minmax(3.5rem,auto) minmax(0,1fr)",
  };

  const rows =
    intentos && intentos.length > 0
      ? intentos.map((it, i) => ({
          nro: it.numero,
          hora: formatCierreHourAmPm(it.registradoEn),
          total: it.total,
          ultimo: i === intentos.length - 1,
        }))
      : montosFallback.map((total, i) => ({
          nro: i + 1,
          hora: formatCierreHourAmPm(fallbackHoraIso),
          total,
          ultimo: i === montosFallback.length - 1,
        }));

  return (
    <div className={`${TICKET_CIERRE_TABLA_MONO_BASE} leading-tight mt-1`}>
      <div
        className={`${TICKET_CIERRE_TABLA_GRID} border-t border-solid border-gray-800`}
        style={colStyle}
      >
        <div className={`${head} tabular-nums whitespace-nowrap ${sep}`}>
          Nro.
        </div>
        <div className={`${head} tabular-nums whitespace-nowrap ${sep}`}>
          Hora
        </div>
        <div className={`${head} tabular-nums whitespace-nowrap`}>
          Total Declarado
        </div>
        <hr className={TICKET_CIERRE_TABLA_HR_FILA} aria-hidden />

        {rows.map((row) => (
          <Fragment key={`intento-${row.nro}`}>
            <div
              className={`${cell} tabular-nums text-gray-900 whitespace-nowrap ${sep}`}
            >
              {row.nro}
            </div>
            <div
              className={`${cell} tabular-nums text-gray-800 whitespace-nowrap ${sep}`}
            >
              {row.hora}
            </div>
            <div
              className={`${cell} tabular-nums font-black whitespace-nowrap`}
            >
              {formatTicketMoney(row.total)}
              {row.ultimo ? " (Últ. Fall)" : ""}
            </div>
          </Fragment>
        ))}
      </div>
    </div>
  );
}

export function TicketTablaComparacionArqueo({
  filas,
}: {
  filas: Tabla2ColFila[];
}) {
  return (
    <TicketTabla2ColGrupos
      colIzqHeader=""
      colDerHeader=""
      showHeader={false}
      grupos={[filas]}
      closeBottom={false}
    />
  );
}

/** Resumen operativo del turno, separado del Top 5 oficial. */
export function TicketResumenOperativoTurno({
  resumen,
  totalCobradoVentas,
  totalEfectivo = 0,
  totalDigital = 0,
  recaudoTotal = 0,
  propinaTotal,
  movimientos = {},
  arqueo = null,
  baseApertura = null,
}: {
  resumen: CierreTicketModel["resumenOperativo"];
  totalCobradoVentas: number;
  /** Total cobrado en efectivo (se muestra entre artículos y el total). */
  totalEfectivo?: number;
  /** Total cobrado por canales digitales (Nequi + Daviplata + Tarjeta). */
  totalDigital?: number;
  /** Recaudo total del turno incl. ingresos manuales y abonos (informativo). */
  recaudoTotal?: number;
  propinaTotal: number;
  /** Movimientos del turno (solo se muestran los > 0): INGRESO, abonos, retiros, gastos, devoluciones. */
  movimientos?: {
    ingresosManualesEfectivo?: number;
    abonosCreditoEfectivo?: number;
    retiros?: number;
    gastos?: number;
    devolucionesEfectivo?: number;
  };
  /** Resultado del arqueo — siempre visible (cuadrado si diferencia = 0). */
  arqueo?: {
    esperadoGaveta: number;
    contadoReal: number;
    diferenciaArqueo: number;
  } | null;
  /**
   * Snapshot de la apertura del turno: resumen compacto de la cadena
   * base dejada por el cierre anterior → base real con la que abrió → decisión.
   */
  baseApertura?: BaseAperturaTurno | null;
}) {
  const filasResumen: Tabla2ColFila[] = [
    {
      label: "Ventas realizadas",
      value: String(resumen.ventasActivasCount),
    },
    {
      label: "Artículos vendidos",
      value: String(resumen.totalArticulosVendidos),
    },
  ];
  if (totalEfectivo > 0) {
    filasResumen.push({
      label: "Total Efectivo",
      value: formatTicketMoney(totalEfectivo),
    });
  }
  if (totalDigital > 0) {
    filasResumen.push({
      label: "Total Digital",
      value: formatTicketMoney(totalDigital),
    });
  }
  filasResumen.push({
    label: "Total cobrado en ventas",
    value: formatTicketMoney(totalCobradoVentas),
  });
  if (recaudoTotal > totalCobradoVentas) {
    filasResumen.push({
      label: "Recaudo total (incl. ingresos/abonos)",
      value: formatTicketMoney(recaudoTotal),
      valueClassName: "text-gray-500",
    });
  }
  if (propinaTotal > 0) {
    filasResumen.push({
      label: "Propinas recibidas",
      value: formatTicketMoney(propinaTotal),
    });
  }
  const movimientosFilas: Tabla2ColFila[] = [];
  if ((movimientos.ingresosManualesEfectivo ?? 0) > 0) {
    movimientosFilas.push({
      label: "+ INGRESO (Mov. de Caja)",
      value: `+${formatTicketMoney(movimientos.ingresosManualesEfectivo ?? 0)}`,
      valueClassName: "text-green-700",
    });
  }
  if ((movimientos.abonosCreditoEfectivo ?? 0) > 0) {
    movimientosFilas.push({
      label: "+ ABONOS A CRÉDITO (efectivo)",
      value: `+${formatTicketMoney(movimientos.abonosCreditoEfectivo ?? 0)}`,
      valueClassName: "text-green-700",
    });
  }
  if ((movimientos.retiros ?? 0) > 0) {
    movimientosFilas.push({
      label: "− RETIRO (Mov. de Caja)",
      value: `−${formatTicketMoney(movimientos.retiros ?? 0)}`,
    });
  }
  if ((movimientos.gastos ?? 0) > 0) {
    movimientosFilas.push({
      label: "− GASTOS (Mov. de Caja)",
      value: `−${formatTicketMoney(movimientos.gastos ?? 0)}`,
    });
  }
  if ((movimientos.devolucionesEfectivo ?? 0) > 0) {
    movimientosFilas.push({
      label: "− DEVOLUCIÓN (Mov. de Caja)",
      value: `−${formatTicketMoney(movimientos.devolucionesEfectivo ?? 0)}`,
    });
  }

  const grupos: Tabla2ColFila[][] = [];
  if (filasResumen.length > 0) grupos.push(filasResumen);
  if (movimientosFilas.length > 0) grupos.push(movimientosFilas);
  if (resumen.horaPicoRango) {
    grupos.push([
      {
        label: "Hora pico",
        value: resumen.horaPicoRango,
      },
      {
        label: `En esa hora: ${resumen.horaPicoVentasCount} ventas`,
        value: formatTicketMoney(resumen.horaPicoMonto),
        strong: true,
      },
    ]);
  }
  if (arqueo) {
    grupos.push([
      {
        label: "Efectivo esperado en gaveta",
        value: formatTicketMoney(arqueo.esperadoGaveta),
      },
      {
        label: "Efectivo contado",
        value: formatTicketMoney(arqueo.contadoReal),
      },
      {
        label:
          arqueo.diferenciaArqueo < 0
            ? "Faltante físico de caja"
            : arqueo.diferenciaArqueo > 0
              ? "Sobrante físico de caja"
              : "Arqueo cuadrado",
        value:
          arqueo.diferenciaArqueo === 0
            ? formatTicketMoney(0)
            : `${arqueo.diferenciaArqueo > 0 ? "+" : "−"}${formatTicketMoney(Math.abs(arqueo.diferenciaArqueo))}`,
        strong: true,
        valueClassName:
          arqueo.diferenciaArqueo >= 0 ? "text-green-700" : "text-red-700",
      },
    ]);
  }

  // Cadena de verificación de la base: el turno abrió con la base dejada por el
  // cierre anterior, se agregó más, o se retiró/cambió. Se muestra aquí de forma
  // compacta; el bloque completo vive en "Movimiento del efectivo".
  if (baseApertura) {
    const tieneBaseAnterior = baseApertura.baseCierreAnterior !== null;
    const filasBase: Tabla2ColFila[] = [];
    if (tieneBaseAnterior) {
      filasBase.push({
        label: "Base dejada (cierre anterior)",
        value: formatTicketMoney(baseApertura.baseCierreAnterior ?? 0),
      });
    } else {
      filasBase.push({
        label: "Base del cierre anterior",
        value: "No se dejó base",
        valueClassName: "text-gray-500",
      });
    }
    filasBase.push({
      label: "Base con la que abrió",
      value: formatTicketMoney(baseApertura.montoApertura),
      strong: true,
    });
    filasBase.push({
      label: "Decisión",
      value: baseApertura.decisionBase,
      valueClassName:
        baseApertura.variacionBase !== 0 ||
        baseApertura.decisionBase === "base reorganizada"
          ? baseApertura.variacionBase < 0
            ? "text-red-700"
            : "text-amber-700"
          : "text-green-700",
    });
    if (tieneBaseAnterior) {
      const variacion = baseApertura.variacionBase;
      filasBase.push({
        label: "Variación vs cierre anterior",
        value:
          variacion === 0
            ? formatTicketMoney(0)
            : `${variacion > 0 ? "+" : "−"}${formatTicketMoney(Math.abs(variacion))}`,
        valueClassName:
          variacion === 0
            ? "text-gray-600"
            : variacion > 0
              ? "text-amber-700"
              : "text-red-700",
      });
    }
    grupos.push(filasBase);
  }

  return (
    <div className="space-y-0">
      <TicketSectionTitle title="Resumen del turno" />
      <TicketTabla2ColGrupos
        colIzqHeader="Concepto"
        colDerHeader="Valor"
        grupos={grupos}
        closeBottom={false}
      />
    </div>
  );
}

/** Etiqueta legible de la opción elegida al abrir (botones del asistente). */
const ORIGEN_BASE_APERTURA_LABEL: Record<BaseAperturaTurno["origen"], string> =
  {
    conservada: "Continuar con esta base",
    agregada: "Agregar más base",
    reemplazada: "Retirar o cambiar",
    manual: "Monto ingresado manualmente",
    precargada: "Precargada (sin opción registrada)",
  };

/**
 * Verificación de la base de apertura: cadena de auditoría entre la base que
 * dejó el cierre anterior y la base con la que abrió el turno que cierra.
 * Se ubica tras «Movimiento del efectivo» y siempre que el cierre tenga el
 * snapshot (`aperturaBase`) registrado durante la apertura de la sesión.
 */
export function TicketVerificacionBaseApertura({
  apertura,
}: {
  apertura: BaseAperturaTurno;
}) {
  const tieneBaseAnterior = apertura.baseCierreAnterior !== null;
  const filas: Tabla2ColFila[] = [];
  if (tieneBaseAnterior) {
    filas.push({
      label: "Base dejada en el cierre anterior",
      value: formatTicketMoney(apertura.baseCierreAnterior ?? 0),
    });
  } else {
    filas.push({
      label: "Base del cierre anterior",
      value: "No se dejó base",
      valueClassName: "text-gray-500",
    });
  }
  filas.push({
    label: "Opción elegida al abrir",
    value: ORIGEN_BASE_APERTURA_LABEL[apertura.origen] ?? apertura.origen,
  });
  filas.push({
    label: "Decisión",
    value: apertura.decisionBase,
  });
  filas.push({
    label: "Base con la que abrió el turno",
    value: formatTicketMoney(apertura.montoApertura),
    strong: true,
  });
  if (tieneBaseAnterior) {
    filas.push({
      label: "Variación vs cierre anterior",
      value:
        apertura.variacionBase === 0
          ? formatTicketMoney(0)
          : `${apertura.variacionBase > 0 ? "+" : "−"}${formatTicketMoney(Math.abs(apertura.variacionBase))}`,
      valueClassName:
        apertura.variacionBase > 0
          ? "text-amber-700"
          : apertura.variacionBase < 0
            ? "text-red-700"
            : "text-green-700",
    });
  }

  const baseModificada =
    tieneBaseAnterior &&
    (apertura.variacionBase !== 0 ||
      apertura.decisionBase === "base reorganizada");
  const avisoPorDebajo = apertura.variacionBase < 0;

  return (
    <>
      <TicketTabla2ColGrupos
        colIzqHeader="Concepto"
        colDerHeader="Valor"
        grupos={[filas]}
        closeBottom={false}
      />
      {baseModificada ? (
        <p
          className={`mx-1 rounded border px-2 py-1 text-center text-[8px] font-black uppercase tracking-wide sm:text-[9px] ${
            avisoPorDebajo
              ? "border-red-300 bg-red-50 text-red-800"
              : "border-amber-300 bg-amber-50 text-amber-800"
          }`}
        >
          Aviso: la base con la que abrió este turno no coincide con la dejada
          en el cierre anterior ({apertura.decisionBase}). Verifica el efectivo
          físico.
        </p>
      ) : null}
      {apertura.conteoBaseApertura ? (
        <>
          <TicketSubsectionLabel>
            Composición física de la apertura
          </TicketSubsectionLabel>
          <TicketTablaBilletesMonedas
            conteo={apertura.conteoBaseApertura}
            totalLabel="Total base de apertura"
          />
        </>
      ) : null}
    </>
  );
}

export function TicketSeccionTopProductos({
  resumen,
}: {
  resumen: CierreTicketModel["resumenOperativo"];
}) {
  if (resumen.topProductos.length === 0) return null;

  return (
    <div className="space-y-0">
      <TicketSectionTitle title="Top 5 productos más vendidos" />
      <TicketTablaTopProductos
        productos={resumen.topProductos}
        totalUnidades={resumen.totalUnidadesTop5}
        ventasActivasCount={resumen.ventasActivasCount}
      />
    </div>
  );
}

/** Bloque 7 — propinas cobradas con título `sectionSub` + tabla Método | Ingreso. */
export function TicketSeccionPropinasCobradas({
  propinas,
  totalPropinas,
}: {
  propinas: MontosPorMetodoPago;
  totalPropinas: number;
}) {
  return (
    <>
      <TicketSectionTitle title="Propinas cobradas (Servicio / Propina)" />
      <TicketTablaPropinasCobradas
        propinas={propinas}
        totalPropinas={totalPropinas}
      />
    </>
  );
}

/** Tabla 4 cols — rendimiento por empleado (sin título; va bajo `sectionMajor`). */
export function TicketTablaRendimientoEmpleados({
  rows,
  ventasActivasCount,
  propinaTotal,
}: {
  rows: CierreTicketModel["rendimientoEmpleados"];
  ventasActivasCount: number;
  propinaTotal: number;
}) {
  const sep = TICKET_CIERRE_TABLA_COL_SEP;
  const headLeft = TICKET_CIERRE_TABLA_HEAD_METODO_IZQ;
  const headRight = TICKET_CIERRE_TABLA_HEAD_METODO_DER;
  const cellLeft = TICKET_CIERRE_TABLA_DATA_METODO_IZQ;
  const cellRight = TICKET_CIERRE_TABLA_DATA_METODO_DER;

  return (
    <div className={`${TICKET_CIERRE_TABLA_MONO_BASE} leading-tight`}>
      <div
        className={`${TICKET_CIERRE_TABLA_GRID} border-t border-solid border-gray-800`}
        style={TICKET_CIERRE_TABLA_COLUMNAS_RENDIMIENTO_EMPLEADO}
      >
        <div className={`${headLeft} ${sep}`}>Empleado</div>
        <div className={`${headRight} ${sep}`}>Ventas</div>
        <div className={`${headRight} ${sep}`}># Ventas</div>
        <div className={headRight}>Total propinas</div>
        <hr className={TICKET_CIERRE_TABLA_HR_FILA} aria-hidden />

        {rows.map((row) => (
          <Fragment key={row.userName}>
            <div className={`${cellLeft} font-medium ${sep}`}>
              {row.userName}
            </div>
            <div className={`${cellRight} ${sep}`}>
              {formatTicketMoney(row.ventasTotal)}
            </div>
            <div className={`${cellRight} font-normal ${sep}`}>
              {row.ventasCount} v
            </div>
            <div className={cellRight}>
              {row.propinasTotal > 0
                ? formatTicketMoney(row.propinasTotal)
                : "—"}
            </div>
          </Fragment>
        ))}

        {rows.length > 0 ? (
          <>
            <hr className={TICKET_CIERRE_TABLA_HR_FILA} aria-hidden />
            <div className={`${cellLeft} font-black uppercase ${sep}`}>
              Totales
            </div>
            <div className={`${cellRight} font-black ${sep}`}>
              {formatTicketMoney(rows.reduce((s, r) => s + r.ventasTotal, 0))}
            </div>
            <div className={`${cellRight} font-black ${sep}`}>
              {ventasActivasCount} v
            </div>
            <div className={`${cellRight} font-black`}>
              {formatTicketMoney(propinaTotal)}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

/** Bloque 8 — rendimiento por empleado con título `sectionMajor`. */
export function TicketSeccionRendimientoEmpleado({
  rows,
  ventasActivasCount,
  propinaTotal,
}: {
  rows: CierreTicketModel["rendimientoEmpleados"];
  ventasActivasCount: number;
  propinaTotal: number;
}) {
  return (
    <>
      <TicketSectionTitle title={`Rendimiento por empleado (${rows.length})`} />
      <TicketTablaRendimientoEmpleados
        rows={rows}
        ventasActivasCount={ventasActivasCount}
        propinaTotal={propinaTotal}
      />
    </>
  );
}

function fmtPropinaMetodoEmpleado(label: string, monto: number): string {
  return `${label}: ${formatTicketMoney(monto)}`;
}

/** Tarjeta propina por empleado (2 cols, patrón `propinaEmpleadoCard`). */
export function TicketPropinaEmpleadoCard({
  row,
}: {
  row: CierreTicketModel["propinaPorEmpleado"][number];
}) {
  const sep = TICKET_CIERRE_TABLA_COL_SEP;
  const cellLeft = TICKET_CIERRE_TABLA_DATA_METODO_IZQ;
  const cellRight = TICKET_CIERRE_TABLA_DATA_METODO_DER;

  return (
    <div
      className={`${TICKET_CIERRE_TABLA_GRID}`}
      style={BILLETES_MONEDAS_COLS}
    >
      <div className={`${cellLeft} font-black uppercase ${sep}`}>
        {row.userName}
      </div>
      <div className={`${cellRight} font-black uppercase`}>
        TOTAL: {formatTicketMoney(row.total)}
      </div>
      <div className={`${cellLeft} ${sep}`}>
        {fmtPropinaMetodoEmpleado("Efectivo", row.porMetodo.efectivo)}
      </div>
      <div className={cellRight}>
        {fmtPropinaMetodoEmpleado("Nequi", row.porMetodo.nequi)}
      </div>
      <div className={`${cellLeft} ${sep}`}>
        {fmtPropinaMetodoEmpleado("Daviplata", row.porMetodo.daviplata)}
      </div>
      <div className={cellRight}>
        {fmtPropinaMetodoEmpleado("Tarjeta", row.porMetodo.tarjeta)}
      </div>
      <hr className={TICKET_CIERRE_TABLA_HR_PROPINA_EMPLEADO} aria-hidden />
    </div>
  );
}

/** Bloque 9 — matriz propinas por empleado + total general. */
export function TicketSeccionPropinasPorEmpleado({
  rows,
  totalPropinas,
}: {
  rows: CierreTicketModel["propinaPorEmpleado"];
  totalPropinas: number;
}) {
  return (
    <>
      <TicketSectionTitle title="Detalle de propinas por empleado" />
      <div
        className={`${TICKET_CIERRE_TABLA_MONO_BASE} leading-tight border-t border-solid border-gray-500`}
      >
        {rows.map((row) => (
          <TicketPropinaEmpleadoCard key={row.usuarioId} row={row} />
        ))}
      </div>
      <div className="-mt-px">
        <TicketTabla2ColGrupos
          colIzqHeader=""
          colDerHeader=""
          showHeader={false}
          grupos={[
            [
              {
                label: "TOTAL PROPINAS",
                value: formatTicketMoney(totalPropinas),
                strong: true,
              },
            ],
          ]}
          closeBottom={false}
        />
      </div>
    </>
  );
}

function filaMontoNegativo(label: string, monto: number): Tabla2ColFila {
  return { label, value: `−${formatTicketMoney(monto)}` };
}

function filasRecaudoTurno(
  ventas: number,
  ingresos: number,
  retiros: number,
  abonos: number,
  conPropina: boolean,
): Tabla2ColFila[] {
  const filas: Tabla2ColFila[] = [
    {
      label: conPropina ? "Ventas (Incluye Propina)" : "Ventas",
      value: formatTicketMoney(ventas),
    },
  ];
  if (ingresos > 0) {
    filas.push({
      label: "(+) INGRESO (Mov. de Caja)",
      value: formatTicketMoney(ingresos),
    });
  }
  if (retiros > 0) {
    filas.push({
      label: "(+) RETIRO (Mov. de Caja)",
      value: formatTicketMoney(retiros),
    });
  }
  if (abonos > 0) {
    filas.push({
      label: "(+) Abonos a crédito",
      value: formatTicketMoney(abonos),
    });
  }
  return filas;
}

/** Sub-tabla recaudo efectivo/digital (sin cabecera de columnas). */
function TicketTablaRecaudoTurno({
  titulo,
  filas,
  totalLabel,
  totalValue,
}: {
  titulo: string;
  filas: Tabla2ColFila[];
  totalLabel: string;
  totalValue: number;
}) {
  return (
    <div>
      <p
        className={`${TICKET_CIERRE_SUBSECTION_LABEL} ticket-cierre-grid-cell border border-b-0 border-solid border-gray-800 px-2 py-1.5 sm:px-2.5 sm:py-2`}
      >
        {titulo}
      </p>
      <TicketTabla2ColGrupos
        colIzqHeader=""
        colDerHeader=""
        showHeader={false}
        grupos={[
          filas,
          [
            {
              label: totalLabel,
              value: formatTicketMoney(totalValue),
              strong: true,
            },
          ],
        ]}
        closeBottom={false}
      />
    </div>
  );
}

/** Bloque 11 — cuadrante de caja neto. */
export function TicketSeccionCuadranteNeto({
  contadoReal,
  baseReservada,
  propinasEfectivo,
  efectivoNetoEntregar,
  decisionBaseRegistrada,
}: {
  contadoReal: number;
  baseReservada: number;
  propinasEfectivo: number;
  efectivoNetoEntregar: number;
  decisionBaseRegistrada: boolean;
}) {
  const cuerpo: Tabla2ColFila[] = [
    {
      label: "Total contado en efectivo",
      value: formatTicketMoney(contadoReal),
    },
  ];
  if (baseReservada > 0) {
    cuerpo.push(
      filaMontoNegativo(
        decisionBaseRegistrada
          ? "(−) Base elegida (Se queda)"
          : "(−) Base registrada (Cierre anterior)",
        baseReservada,
      ),
    );
  } else {
    cuerpo.push({
      label: "Base para siguiente turno",
      value: "$0 — No se dejó base",
    });
  }
  if (propinasEfectivo > 0) {
    cuerpo.push(
      filaMontoNegativo("(−) Propinas efect. (Apartar)", propinasEfectivo),
    );
  }

  const pie: Tabla2ColFila[] = [
    {
      label: "Efectivo neto a entregar",
      value: formatTicketMoney(efectivoNetoEntregar),
      strong: true,
    },
  ];

  return (
    <>
      <TicketSectionTitle title="Dinero a entregar" />
      <TicketTabla2ColGrupos
        colIzqHeader="Concepto"
        colDerHeader="Valor"
        grupos={[cuerpo, pie]}
        closeBottom={false}
      />
    </>
  );
}

/** Bloque 12 — auditoría de flujo operativo. */
export function TicketSeccionAuditoriaFlujo({
  audit,
  totalRecaudado,
}: {
  audit: CierreAuditMetrics;
  totalRecaudado: number;
}) {
  const retiros = audit.totalRetirosSeguridad + audit.retirosOtros;

  return (
    <>
      <div className="mb-2 border-b border-solid border-gray-800">
        <TicketSectionTitle title="Recaudo total del turno" />
      </div>
      <TicketTablaRecaudoTurno
        titulo="Recaudo del turno — Efectivo"
        filas={filasRecaudoTurno(
          audit.ventasEfectivoRecaudo,
          audit.totalIngresosManualesEfectivo,
          retiros,
          audit.totalAbonosCreditoEfectivo,
          audit.propinasEfectivo > 0,
        )}
        totalLabel="Total efectivo"
        totalValue={audit.recaudoEfectivo}
      />
      <div className="mt-2">
        <TicketTablaRecaudoTurno
          titulo="Recaudo del turno — Digital"
          filas={filasRecaudoTurno(
            audit.ventasDigitalRecaudo,
            audit.totalIngresosManualesDigital,
            0,
            audit.totalAbonosCreditoDigital,
            audit.ventasDigitalRecaudo > audit.totalVentasDigitalNetas,
          )}
          totalLabel="Total digital"
          totalValue={audit.recaudoDigital}
        />
      </div>
      <div className="-mt-px">
        <TicketTabla2ColGrupos
          colIzqHeader=""
          colDerHeader=""
          showHeader={false}
          grupos={[
            [
              {
                label: "TOTAL RECAUDADO DEL NEGOCIO",
                value: formatTicketMoney(totalRecaudado),
                strong: true,
              },
            ],
          ]}
          closeBottom={false}
        />
      </div>
    </>
  );
}

/** Bloque 13 — consolidado del turno. */
export function TicketSeccionConsolidadoTurno({
  totalRecaudado,
  audit,
  dineroEsperadoCierre,
  dineroEncontradoCierre,
  diferenciaFinancieraCierre,
}: {
  totalRecaudado: number;
  audit: CierreAuditMetrics;
  dineroEsperadoCierre: number;
  dineroEncontradoCierre: number;
  diferenciaFinancieraCierre: number;
}) {
  const cuerpo: Tabla2ColFila[] = [
    {
      label: "Total recaudado del turno",
      value: formatTicketMoney(totalRecaudado),
      strong: true,
    },
  ];
  if (audit.totalGastos > 0) {
    cuerpo.push(filaMontoNegativo("(−) Total gastos turno", audit.totalGastos));
  }
  if (audit.totalDevoluciones > 0) {
    cuerpo.push(
      filaMontoNegativo("(−) Total devoluciones", audit.totalDevoluciones),
    );
  }

  const pie: Tabla2ColFila[] = [
    {
      label: "Dinero esperado al cierre",
      value: formatTicketMoney(dineroEsperadoCierre),
      strong: true,
    },
    {
      label: "Dinero encontrado al cierre",
      value: formatTicketMoney(dineroEncontradoCierre),
      strong: true,
    },
  ];
  if (diferenciaFinancieraCierre !== 0) {
    pie.push({
      label:
        diferenciaFinancieraCierre < 0
          ? "Faltante total del negocio"
          : "Sobrante total del negocio",
      value: `${diferenciaFinancieraCierre >= 0 ? "+" : "−"}${formatTicketMoney(Math.abs(diferenciaFinancieraCierre))}`,
      valueClassName: "text-red-700",
    });
  }

  return (
    <>
      <TicketSectionTitle title="Resumen financiero" />
      <TicketTabla2ColGrupos
        colIzqHeader="Descripción"
        colDerHeader="Valor"
        grupos={[cuerpo, pie]}
        closeBottom={false}
      />
      {audit.totalRetirosSeguridad + audit.retirosOtros > 0 ? (
        <p className="px-1 pt-1 text-[7px] sm:text-[8px] leading-snug text-gray-600">
          Los retiros reducen la gaveta, pero siguen siendo dinero del negocio;
          por eso se integran al recaudo y no se restan nuevamente aquí.
        </p>
      ) : null}
    </>
  );
}

/** Bloque opcional — gastos del dueño pagados fuera de gaveta (día contable). */
export function TicketSeccionGastosExternosDia({
  gastos,
  total,
}: {
  gastos: GastoExterno[];
  total: number;
}) {
  if (gastos.length === 0 || total <= 0) return null;

  const filas: Tabla2ColFila[] = [
    ...gastos.map((g) => ({
      label: `${g.categoria}${g.descripcion ? ` · ${g.descripcion}` : ""} (${gastoExternoMetodoLabel(g.metodoPago, g.metodoPagoLabel)})`,
      value: `−${formatTicketMoney(g.monto)}`,
    })),
    filaMontoNegativo("Total gastos del dueño (día)", total),
  ];

  return (
    <>
      <TicketSectionTitle title="Gastos del dueño (fuera de caja · día)" />
      <TicketTabla2ColGrupos
        colIzqHeader="Concepto"
        colDerHeader="Monto"
        grupos={[filas]}
        closeBottom={false}
      />
      <p className="text-[9px] sm:text-[10px] text-gray-500 dark:text-slate-400 italic px-1 mt-1">
        No afectan arqueo ni gaveta. Restan de la utilidad neta del día.
      </p>
    </>
  );
}
